import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogById,
  toggleLike,
  addComment,
  deleteComment,
} from "../../api/blog";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import ShareModal from "../../components/common/ShareModal";
import Loader from "../../components/ui/Loader";
import {
  Loader2,
  ArrowLeft,
  Calendar,
  User,
  Share2,
  Heart,
  MessageCircle,
  Send,
  Trash2,
  Copy,
  Linkedin,
  Clock,
  Check,
  Twitter,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { format, parseISO, isValid } from "date-fns";
import { toast } from "react-hot-toast";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedBackground from "../../components/ui/AnimatedBackground";

export default function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [likeState, setLikeState] = useState({
    count: 0,
    isLiked: false,
    isProcessing: false,
  });

  const formatDate = (value) => {
    try {
      const date = typeof value === "string" ? parseISO(value) : new Date(value);
      if (!isValid(date)) return "Just now";
      return format(date, "MMM d, yyyy • h:mm a");
    } catch { return "Just now"; }
  };

  const formatBlogDate = (value) => {
    try {
      const date = typeof value === "string" ? parseISO(value) : new Date(value);
      if (!isValid(date)) return "—";
      return format(date, "MMMM d, yyyy");
    } catch { return "—"; }
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const data = await getBlogById(id);
        setBlog(data);
        setLikeState({
          count: data?._count?.likes || 0,
          isLiked: data?.isLiked || false,
          isProcessing: false,
        });
      } catch {
        toast.error("Blog not found");
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, navigate]);

  const handleLike = async () => {
    if (!user) return toast.error("Login to like");
    if (likeState.isProcessing) return;

    const prevLiked = likeState.isLiked;
    const prevCount = likeState.count;

    setLikeState((p) => ({
      ...p,
      isLiked: !prevLiked,
      count: prevLiked ? p.count - 1 : p.count + 1,
      isProcessing: true,
    }));

    try {
      const res = await toggleLike(id);
      setLikeState({ isLiked: res.isLiked, count: res.likesCount, isProcessing: false });
    } catch {
      setLikeState({ isLiked: prevLiked, count: prevCount, isProcessing: false });
      toast.error("Failed to like");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmittingComment(true);
    try {
      const newComment = await addComment(id, comment);
      setBlog((prev) => ({
        ...prev,
        comments: [newComment, ...prev.comments],
        _count: { ...prev._count, comments: (prev._count?.comments || 0) + 1 },
      }));
      setComment("");
      toast.success("Comment added");
    } catch {
      toast.error("Failed to comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      setBlog((prev) => ({
        ...prev,
        comments: prev.comments.filter((c) => c.id !== commentId),
        _count: { ...prev._count, comments: prev._count.comments - 1 },
      }));
      toast.success("Comment deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <Loader />;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-transparent relative"
    >
      <AnimatedBackground />
      <div className="absolute top-0 left-0 w-full h-[60vh] bg-gradient-to-b from-primary/5 to-transparent pointer-events-none -z-10" />

      {/* Header Container */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 md:pt-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button variant="ghost" className="mb-8 hover:bg-muted font-bold" onClick={() => navigate("/blog")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Browse Articles
          </Button>
        </motion.div>

        <div className="space-y-8 mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Badge className="px-4 py-1.5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200 uppercase font-black tracking-widest text-[10px]">Article</Badge>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[0.9] text-foreground"
          >
            {blog.title}
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap items-center gap-8 py-6 border-y border-border/50"
          >
            <div className="flex items-center gap-3">
               <Avatar className="w-10 h-10 border-2 border-primary/20">
                 <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold">
                   {blog.author?.email?.[0].toUpperCase()}
                 </AvatarFallback>
               </Avatar>
               <div>
                  <p className="text-sm font-black text-foreground">{blog.author?.email?.split("@")[0]}</p>
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">{formatBlogDate(blog.createdAt)}</p>
               </div>
            </div>
            <div className="hidden sm:block w-[1px] h-8 bg-border/50" />
            <div className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
               <Clock className="w-4 h-4" /> {Math.ceil((blog.content?.length || 0) / 1000)} min read
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={handleLike} disabled={likeState.isProcessing} variant={likeState.isLiked ? "default" : "outline"} className="rounded-xl shadow-lg shadow-primary/5 font-black">
                  <Heart className={`w-4 h-4 mr-2 ${likeState.isLiked ? "fill-current" : ""}`} /> {likeState.count}
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="rounded-xl" onClick={() => setShareDialogOpen(true)}><Share2 className="w-4 h-4" /></Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Hero Image */}
      {blog.coverImage && (
        <motion.div 
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto px-4 sm:px-6 mb-16"
        >
          <div className="aspect-[21/9] w-full overflow-hidden rounded-[2.5rem] border shadow-2xl relative group">
            <img src={blog.coverImage} alt={blog.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </motion.div>
      )}

      {/* Content Section */}
      <section className="pb-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="border-0 shadow-none bg-transparent">
            <CardContent className="p-0">
              <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="prose prose-lg dark:prose-invert max-w-none mb-24 leading-relaxed font-medium text-foreground/80 dark:text-gray-300"
              >
                {blog.content}
              </motion.div>

              {/* Interaction Panel */}
              <div className="border-t pt-16 mt-16">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-8 mb-12"
                >
                   <div>
                      <h3 className="text-3xl font-black tracking-tight text-foreground">Community Discussion</h3>
                      <p className="text-muted-foreground font-medium mt-1">Share your thoughts on "{blog.title}"</p>
                   </div>
                   <div className="flex items-center gap-2 text-primary font-black px-4 py-2 bg-primary/5 rounded-2xl border border-primary/10">
                      <MessageCircle className="w-5 h-5" /> {blog._count?.comments || 0} Comments
                   </div>
                </motion.div>

                {user ? (
                  <motion.form 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    onSubmit={handleCommentSubmit} 
                    className="mb-12 group"
                  >
                    <div className="relative">
                      <textarea
                        className="w-full min-h-[160px] p-6 rounded-[2rem] border border-border bg-card/40 backdrop-blur-md text-foreground placeholder:text-muted-foreground/50 resize-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all duration-300 shadow-inner"
                        placeholder="Join the conversation..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end mt-4">
                      <Button type="submit" disabled={submittingComment || !comment.trim()} className="h-12 px-8 rounded-2xl bg-primary text-white font-black shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all">
                        {submittingComment ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Post Comment</>}
                      </Button>
                    </div>
                  </motion.form>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="mb-12 p-10 rounded-[2.5rem] border border-dashed border-primary/20 bg-primary/5 text-center space-y-4"
                  >
                    <p className="text-lg font-bold text-foreground">Authentication Required</p>
                    <p className="text-muted-foreground max-w-sm mx-auto">Please sign in to your account to leave a comment and interact with the community.</p>
                    <Button onClick={() => navigate("/login")} className="px-10 rounded-xl h-12 shadow-lg font-black font-bold">Sign In Now</Button>
                  </motion.div>
                )}

                <div className="space-y-8">
                  <AnimatePresence>
                    {blog.comments?.length === 0 ? (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-20 bg-muted/20 rounded-[2.5rem] border border-border/50"
                      >
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4"><MessageCircle className="w-10 h-10 text-muted-foreground/30" /></div>
                        <p className="text-muted-foreground font-black text-lg">No comments yet</p>
                        <p className="text-sm text-muted-foreground/60">Be the first to share your perspective!</p>
                      </motion.div>
                    ) : (
                      blog.comments?.map((c, idx) => (
                        <motion.div 
                          key={c.id} 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: idx * 0.05 }}
                          className="group flex gap-5"
                        >
                          <Avatar className="w-12 h-12 border-2 border-border/50 shadow-sm shrink-0">
                            <AvatarFallback className="bg-muted text-foreground font-black uppercase text-xs">
                              {c.user?.email?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-2">
                            <motion.div 
                              whileHover={{ scale: 1.005 }}
                              className="group/bubble bg-card dark:bg-muted/10 border border-border p-6 rounded-[2rem] rounded-tl-none shadow-sm transition-all hover:shadow-md hover:border-primary/20 backdrop-blur-sm"
                            >
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-black text-foreground">{c.user?.email?.split("@")[0]}</span>
                                <span className="text-[10px] font-bold text-muted-foreground uppercase">{formatDate(c.createdAt)}</span>
                              </div>
                              <p className="text-sm leading-relaxed text-foreground/80 dark:text-gray-300">{c.content}</p>
                            </motion.div>
                            {user?.id === c.userId && (
                              <ConfirmDialog
                                trigger={<button className="text-[10px] font-black uppercase tracking-widest text-destructive/60 hover:text-destructive flex items-center gap-1.5 ml-4 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>}
                                title="Delete comment?"
                                onConfirm={() => handleDeleteComment(c.id)}
                                variant="destructive"
                              />
                            )}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Share Modal integrated */}
      <ShareModal 
        open={shareDialogOpen} 
        onOpenChange={setShareDialogOpen} 
        title={blog.title} 
        url={window.location.href}
        description="Spread the knowledge across your favorite platforms."
      />
    </motion.div>
  );
}
